import React from "react";
import { Button, Step as Stepper } from "semantic-ui-react";
import UsersStep from "./UsersStep";
import DetailsStep from "./DetailsStep";
import FreeFieldsSelectStep from "./FreeFieldsSelectStep";
import { type FFPermissionMap, getFreefieldPermissionMap } from "./PermissionMap";
import { NextStepButton } from "../../components/buttons";
import DetailViewPanel from "components/panels/DetailViewPanel";

interface Props {
  freeFieldProfile: server.dto.FreeFieldProfile
  freeFieldsForProduction: server.dto.FreeFieldCategorySmall[]
  freeFieldsForShow: server.dto.FreeFieldCategorySmall[]
  userToClients: server.dto.UserToClientSmall[]
  listUrl: string
}

interface PagePermission {
  accessible: boolean
  nextPage: Step
}

type Step = "details" | "productions" | "shows" | "users";

const FreeFieldProfileDetailSection = (props: Props) => {
  const environment = props.freeFieldProfile.environment;

  const [step, setStep] = React.useState<Step>("details");
  const [title, setTitle] = React.useState<string>(props.freeFieldProfile.title || "");
  const [permissions, setPermissions] = React.useState<FFPermissionMap>(
    props.freeFieldProfile.permissions ? getFreefieldPermissionMap(props.freeFieldProfile.permissions) : {}
  );
  const [userToClients, setUserToClients] = React.useState<server.dto.UserToClientSmall[]>(props.userToClients || []);
  const [profile, setProfile] = React.useState<server.dto.FreeFieldProfile>(props.freeFieldProfile);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const freeFieldsForProduction = props.freeFieldsForProduction;
  const freeFieldsForShow = props.freeFieldsForShow;

  const hasProductionField = site.currentEnvironment.production.enabled && freeFieldsForProduction.length > 0;
  const isNewProfile = profile.freeFieldProfileId === 0;
  const stepCount = hasProductionField ? 5 : 4;
  const hasTitle = title.trim() !== "";

  const usersStepTitle = environment === "internal"
    ? RESX.UserType.InternalUsers
    : RESX.UserType.ExternalUsers;

  const productionStepEnabled = hasTitle && !isNewProfile && hasProductionField;
  const showStepEnabled = hasTitle && !isNewProfile;
  const usersStepEnabled = hasTitle && !isNewProfile;

  const availablePages: { [page in Step]: PagePermission } = {
    details: {
      accessible: true,
      nextPage: hasProductionField ? "productions" : "shows"
    },
    productions: {
      accessible: productionStepEnabled,
      nextPage: "shows"
    },
    shows: {
      accessible: showStepEnabled,
      nextPage: "users"
    },
    users: {
      accessible: usersStepEnabled,
      nextPage: "users"
    }
  };

  const saveTitle = async () => {
    if (step === "details") {
      if (isLoading) {
        return;
      }

      setIsLoading(true);
      if (isNewProfile) {
        const createOutcome = await window.CoreApi.FreeFieldProfile.CreateProfile({
          environment,
          title
        });

        const newProfile = await window.CoreApi.FreeFieldProfile.GetProfile(createOutcome.data.data);

        setProfile(newProfile.data);
        setPermissions(getFreefieldPermissionMap(newProfile.data.permissions));
      } else {
        await window.CoreApi.FreeFieldProfile.UpdateProfile({
          freeFieldProfileId: profile.freeFieldProfileId,
          title
        });
      }
      setIsLoading(false);
    }
  };

  const onNextStepClick = async (e) => {
    e.preventDefault();
    const nextStep = availablePages[step].nextPage;
    gotoStep(nextStep);
  };

  const gotoStep = async (nextStep: Step) => {
    if (step === nextStep) {
      return;
    }
    await saveTitle();

    window.location.hash = nextStep;
    setStep(nextStep);
  };

  React.useEffect(() => {
    const { hash } = window.location;

    if (hash) {
      const page: string = hash.slice(1).toLowerCase();

      const pagePermission: PagePermission | undefined = availablePages[page];
      if (pagePermission && pagePermission.accessible) {
        setStep(page as Step);
      }
    }
  }, []);

  return (
    <>
      <DetailViewPanel
        title={RESX.FreeField.FreeFieldPermissionProfile}>
        <div className="margin-bottom-big">
          <Stepper.Group widths={stepCount}>
            <Stepper onClick={async () => gotoStep("details")} active={step === "details"}>
              <Stepper.Content>
                <Stepper.Title>{RESX.GeneralLabels.Details}</Stepper.Title>
              </Stepper.Content>
            </Stepper>
            {hasProductionField && <Stepper onClick={async () => gotoStep("productions")} active={step === "productions"} disabled={!productionStepEnabled}>
              <Stepper.Content>
                <Stepper.Title>{site.currentEnvironment.production.singular}</Stepper.Title>
                <Stepper.Description>{RESX.FreeField.FreeFields}</Stepper.Description>
              </Stepper.Content>
            </Stepper>}
            <Stepper onClick={async () => gotoStep("shows")} active={step === "shows"} disabled={!showStepEnabled}>
              <Stepper.Content>
                <Stepper.Title>{site.currentEnvironment.show.singular}</Stepper.Title>
                <Stepper.Description>{RESX.FreeField.FreeFields}</Stepper.Description>
              </Stepper.Content>
            </Stepper>
            <Stepper onClick={async () => gotoStep("users")} active={step === "users"} disabled={!usersStepEnabled}>
              <Stepper.Content>
                <Stepper.Title>{usersStepTitle}</Stepper.Title>
              </Stepper.Content>
            </Stepper>
          </Stepper.Group>
        </div>

        {step === "details" && <DetailsStep title={title} onTitleChange={setTitle} />}
        {step === "productions" && <FreeFieldsSelectStep
          onPermissionsChange={setPermissions}
          permissions={permissions}
          profile={profile}
          freeFields={freeFieldsForProduction}
        />}
        {step === "shows" && <FreeFieldsSelectStep
          onPermissionsChange={setPermissions}
          permissions={permissions}
          profile={profile}
          freeFields={freeFieldsForShow}
        />}
        {step !== "users" && <div className="flex justify-end margin-top-bigger">
          <NextStepButton
            loading={isLoading}
            onClick={onNextStepClick}
            disabled={!hasTitle}
          />
        </div>}
        {step === "users" && <>
          <UsersStep onUsersChanged={setUserToClients} freeFieldProfileId={profile.freeFieldProfileId} environment={profile.environment} userToClients={userToClients} />
          <div className="flex justify-end margin-top-bigger">
            <Button primary onClick={(e) => {
              e.preventDefault();
              window.location.href = props.listUrl;
            }}>
              {RESX.Buttons.BtnOk}
            </Button>
          </div>
        </>}
      </DetailViewPanel>
    </>
  );
};

export default FreeFieldProfileDetailSection;
